import { BrowserDpopKey } from "./dpop";
import {
  EnvironmentConnectSchema,
  EnvironmentEventSchema,
  EnvironmentLink,
  EnvironmentDescriptorSchema,
  WebsocketTicketSchema,
  normalizeEnvironmentLinks,
  parseSnapshot,
  rpcRequest,
  type EnvironmentEvent,
  type WorkspaceSnapshot,
} from "./protocol";
import type { PublicConfig } from "./config";

const RELAY_SCOPES = [
  "relay:read",
  "relay:write",
  "access:read",
  "access:write",
  "orchestration:read",
  "orchestration:operate",
  "terminal:operate",
].join(" ");

const TOKEN_EXCHANGE_GRANT = "urn:ietf:params:oauth:grant-type:token-exchange";
const JWT_TOKEN_TYPE = "urn:ietf:params:oauth:token-type:jwt";
const ACCESS_TOKEN_TYPE = "urn:ietf:params:oauth:token-type:access_token";

type RelayToken = {
  accessToken: string;
  expiresAt: number;
};

export type ConnectedEnvironment = {
  environmentId: string;
  httpBaseUrl: string;
  wsBaseUrl: string;
  credential: string;
  expiresAt?: string;
};

export type RpcSocket = {
  request: (tag: string, payload: unknown) => Promise<unknown>;
  close: () => void;
};

export type TerminalSocket = {
  send: (data: string) => void;
  resize: (cols: number, rows: number) => void;
  close: () => void;
};

type CloudClientOptions = {
  config: PublicConfig;
  accountId: string;
  getClerkToken: () => Promise<string | null>;
  onEvent?: (event: EnvironmentEvent) => void;
  onError?: (error: Error) => void;
};

export class CloudClient {
  private readonly config: PublicConfig;
  private readonly accountId: string;
  private readonly getClerkToken: () => Promise<string | null>;
  private readonly onEvent?: (event: EnvironmentEvent) => void;
  private readonly onError?: (error: Error) => void;
  private dpop: BrowserDpopKey | null = null;
  private relayToken: RelayToken | null = null;

  constructor(options: CloudClientOptions) {
    this.config = options.config;
    this.accountId = options.accountId;
    this.getClerkToken = options.getClerkToken;
    this.onEvent = options.onEvent;
    this.onError = options.onError;
  }

  async listEnvironments(): Promise<EnvironmentLink[]> {
    const relayUrl = this.requireRelayUrl();
    const clerkToken = await this.requireClerkToken();
    const response = await fetch(`${relayUrl}/v1/environments`, {
      headers: { Authorization: `Bearer ${clerkToken}`, Accept: "application/json" },
    });
    await assertOk(response, "Could not load linked environments");
    return normalizeEnvironmentLinks(await response.json());
  }

  async connectEnvironment(environmentId: string): Promise<ConnectedEnvironment> {
    const relayUrl = this.requireRelayUrl();
    const key = await this.key();
    const token = await this.ensureRelayToken(key);
    const url = `${relayUrl}/v1/environments/${encodeURIComponent(environmentId)}/connect`;
    const body = {
      deviceId: `web-${key.thumbprint}`,
      clientProofKeyThumbprint: key.thumbprint,
    };
    const response = await this.dpopFetch(url, "POST", token.accessToken, key, body);
    const parsed = EnvironmentConnectSchema.parse(await response.json());
    const endpoint = parsed.endpoint ?? {};
    const httpBaseUrl = stringValue(endpoint.httpBaseUrl);
    if (!httpBaseUrl) throw new Error("The relay returned no environment HTTP endpoint.");
    const wsBaseUrl = stringValue(endpoint.wsBaseUrl) ?? toWebSocketUrl(httpBaseUrl, "/ws");
    return {
      environmentId: parsed.environmentId,
      httpBaseUrl: httpBaseUrl.replace(/\/$/, ""),
      wsBaseUrl,
      credential: parsed.credential,
      expiresAt: parsed.expiresAt,
    };
  }

  async redeemPairing(environmentUrl: string, pairingToken: string): Promise<ConnectedEnvironment> {
    const key = await this.key();
    const base = environmentUrl.replace(/\/$/, "");
    const url = `${base}/oauth/token`;
    const fields = new URLSearchParams({
      grant_type: TOKEN_EXCHANGE_GRANT,
      subject_token: pairingToken,
      subject_token_type: "urn:codessa:params:oauth:token-type:environment-bootstrap",
      requested_token_type: ACCESS_TOKEN_TYPE,
      scope: "orchestration:read orchestration:operate terminal:operate",
      client_label: "Codessa Cloud browser",
      client_device_type: "browser",
      client_os: navigator.platform || "browser",
    });
    const request = async (nonce?: string) => fetch(url, {
      method: "POST",
      headers: { DPoP: await key.proof("POST", url, nonce), Accept: "application/json" },
      body: fields,
    });
    let response = await request();
    if (response.status === 401 && response.headers.get("dpop-nonce")) response = await request(response.headers.get("dpop-nonce") ?? undefined);
    await assertOk(response, "Could not redeem the pairing token");
    const payload = (await response.json()) as { access_token?: unknown; expires_in?: unknown };
    if (typeof payload.access_token !== "string") throw new Error("The environment returned no access token.");
    const descriptorResponse = await fetch(`${base}/.well-known/codessa/environment`);
    await assertOk(descriptorResponse, "Could not load the paired environment descriptor");
    const descriptor = EnvironmentDescriptorSchema.parse(await descriptorResponse.json());
    return {
      environmentId: descriptor.environmentId,
      httpBaseUrl: base,
      wsBaseUrl: toWebSocketUrl(base),
      credential: payload.access_token,
      expiresAt: new Date(Date.now() + (typeof payload.expires_in === "number" ? payload.expires_in : 300) * 1000).toISOString(),
    };
  }

  async snapshot(environment: ConnectedEnvironment): Promise<WorkspaceSnapshot> {
    const response = await this.environmentFetch(environment, "GET", "/api/orchestration/snapshot");
    return parseSnapshot(await response.json());
  }

  async dispatch(environment: ConnectedEnvironment, command: unknown): Promise<unknown> {
    const response = await this.environmentFetch(
      environment,
      "POST",
      "/api/orchestration/dispatch",
      command,
    );
    return response.status === 204 ? null : response.json();
  }

  async descriptor(environment: ConnectedEnvironment): Promise<unknown> {
    const response = await this.environmentFetch(
      environment,
      "GET",
      "/.well-known/codessa/environment",
    );
    return EnvironmentDescriptorSchema.parse(await response.json());
  }

  async issueWebsocketTicket(
    environment: ConnectedEnvironment,
    terminalId?: string,
  ): Promise<string> {
    const suffix = terminalId ? `?terminalId=${encodeURIComponent(terminalId)}` : "";
    const response = await this.environmentFetch(
      environment,
      "POST",
      `/api/auth/websocket-ticket${suffix}`,
    );
    return WebsocketTicketSchema.parse(await response.json()).ticket;
  }

  async openTerminal(
    environment: ConnectedEnvironment,
    terminalId: string,
    cwd: string,
    cols: number,
    rows: number,
    onEvent: (event: unknown) => void,
  ): Promise<TerminalSocket> {
    await this.environmentFetch(environment, "POST", `/api/terminal/${encodeURIComponent(terminalId)}/open`, {
      cwd,
      cols,
      rows,
    });
    const ticket = await this.issueWebsocketTicket(environment, terminalId);
    const socketUrl = appendQuery(
      toWebSocketUrl(`${environment.httpBaseUrl}/api/terminal/${encodeURIComponent(terminalId)}`),
      "wsTicket",
      ticket,
    );
    return new Promise<TerminalSocket>((resolve, reject) => {
      const socket = new WebSocket(socketUrl);
      socket.onopen = () => resolve({
        send: (data) => socket.send(data),
        resize: (nextCols, nextRows) => {
          void this.environmentFetch(
            environment,
            "POST",
            `/api/terminal/${encodeURIComponent(terminalId)}/resize`,
            { cols: nextCols, rows: nextRows },
          );
        },
        close: () => socket.close(),
      });
      socket.onmessage = (message) => {
        if (typeof message.data !== "string") return;
        try {
          const value = JSON.parse(message.data);
          const event = EnvironmentEventSchema.safeParse(value);
          if (event.success) onEvent(event.data);
        } catch {
          onEvent({ type: "Terminal", payload: { kind: "output", data: message.data } });
        }
      };
      socket.onerror = () => reject(new Error("Terminal WebSocket connection failed."));
    });
  }

  async openRpc(environment: ConnectedEnvironment, onClosed?: () => void): Promise<RpcSocket> {
    const ticket = await this.issueWebsocketTicket(environment);
    const url = appendQuery(toWebSocketUrl(environment.wsBaseUrl), "wsTicket", ticket);
    return new Promise<RpcSocket>((resolve, reject) => {
      const socket = new WebSocket(url);
      const pending = new Map<number, { resolve: (value: unknown) => void; reject: (error: Error) => void }>();
      let nextId = 1;
      let opened = false;
      let closed = false;

      const failPending = (error: Error) => {
        for (const request of pending.values()) request.reject(error);
        pending.clear();
      };

      socket.onopen = () => {
        opened = true;
        resolve({
          request: (tag, payload) => {
            if (closed || socket.readyState !== WebSocket.OPEN) {
              return Promise.reject(new Error("Environment connection is not open."));
            }
            const id = nextId++;
            return new Promise((requestResolve, requestReject) => {
              pending.set(id, { resolve: requestResolve, reject: requestReject });
              socket.send(rpcRequest(id, tag, payload));
            });
          },
          close: () => {
            closed = true;
            failPending(new Error("Environment connection closed."));
            socket.close();
          },
        });
      };

      socket.onmessage = (message) => {
        const values = Array.isArray(message.data) ? message.data : [message.data];
        for (const raw of values) {
          if (typeof raw !== "string") continue;
          let value: unknown;
          try {
            value = JSON.parse(raw);
          } catch {
            this.onError?.(new Error("Environment returned malformed JSON."));
            continue;
          }
          const exit = findExit(value);
          if (exit) {
            const request = pending.get(exit.requestId);
            if (!request) continue;
            pending.delete(exit.requestId);
            if (exit.success) request.resolve(exit.value);
            else request.reject(new Error(exit.error));
            continue;
          }
          const event = EnvironmentEventSchema.safeParse(value);
          if (event.success) this.onEvent?.(event.data);
          else this.onError?.(new Error("Environment returned an unsupported event."));
        }
      };

      socket.onerror = () => {
        const error = new Error("Environment WebSocket connection failed.");
        if (!opened) reject(error);
        failPending(error);
      };
      socket.onclose = () => {
        closed = true;
        const error = new Error("Environment WebSocket connection closed.");
        if (!opened) reject(error);
        failPending(error);
        if (opened) onClosed?.();
      };
    });
  }

  async files(environment: ConnectedEnvironment, cwd: string): Promise<unknown> {
    const rpc = await this.openRpc(environment);
    try {
      return await rpc.request("projects.listEntries", { cwd });
    } finally {
      rpc.close();
    }
  }

  async file(environment: ConnectedEnvironment, cwd: string, relativePath: string): Promise<unknown> {
    const rpc = await this.openRpc(environment);
    try {
      return await rpc.request("projects.readFile", { cwd, relativePath });
    } finally {
      rpc.close();
    }
  }

  async clearSession(): Promise<void> {
    this.relayToken = null;
    this.dpop = null;
  }

  private async key(): Promise<BrowserDpopKey> {
    this.dpop ??= await BrowserDpopKey.load(this.accountId);
    return this.dpop;
  }

  private async requireClerkToken(): Promise<string> {
    const token = await this.getClerkToken();
    if (!token) throw new Error("Clerk did not return a relay token. Sign in again.");
    return token;
  }

  private requireRelayUrl(): string {
    if (!this.config.relayUrl) {
      throw new Error("The Codessa relay is not configured yet.");
    }
    return this.config.relayUrl;
  }

  private async ensureRelayToken(key: BrowserDpopKey): Promise<RelayToken> {
    const relayUrl = this.requireRelayUrl();
    if (this.relayToken && this.relayToken.expiresAt > Date.now() + 30_000) return this.relayToken;
    const clerkToken = await this.requireClerkToken();
    const url = `${relayUrl}/v1/client/dpop-token`;
    const fields = new URLSearchParams({
      grant_type: TOKEN_EXCHANGE_GRANT,
      subject_token: clerkToken,
      subject_token_type: JWT_TOKEN_TYPE,
      requested_token_type: ACCESS_TOKEN_TYPE,
      resource: relayUrl,
      scope: RELAY_SCOPES,
      client_id: "codessa-web",
    });
    let response = await fetch(url, {
      method: "POST",
      headers: { DPoP: await key.proof("POST", url), Accept: "application/json" },
      body: fields,
    });
    if (response.status === 401) {
      const nonce = response.headers.get("dpop-nonce");
      if (nonce) {
        response = await fetch(url, {
          method: "POST",
          headers: { DPoP: await key.proof("POST", url, nonce), Accept: "application/json" },
          body: fields,
        });
      }
    }
    await assertOk(response, "Could not authenticate with the Codessa relay");
    const payload = (await response.json()) as {
      access_token?: unknown;
      expires_in?: unknown;
    };
    if (typeof payload.access_token !== "string") throw new Error("The relay returned no access token.");
    const expiresIn = typeof payload.expires_in === "number" ? payload.expires_in : 300;
    this.relayToken = { accessToken: payload.access_token, expiresAt: Date.now() + expiresIn * 1000 };
    return this.relayToken;
  }

  private async environmentFetch(
    environment: ConnectedEnvironment,
    method: string,
    path: string,
    body?: unknown,
  ): Promise<Response> {
    const key = await this.key();
    const url = `${environment.httpBaseUrl}${path}`;
    const authorization = `DPoP ${environment.credential}`;
    return this.dpopFetch(url, method, environment.credential, key, body, authorization);
  }

  private async dpopFetch(
    url: string,
    method: string,
    accessToken: string,
    key: BrowserDpopKey,
    body?: unknown,
    authorization = `DPoP ${accessToken}`,
  ): Promise<Response> {
    const request = async (nonce?: string) =>
      fetch(url, {
        method,
        headers: {
          Authorization: authorization,
          DPoP: await key.proof(method, url, nonce, accessToken),
          Accept: "application/json",
          ...(body === undefined ? {} : { "Content-Type": "application/json" }),
        },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    let response = await request();
    if (response.status === 401) {
      const nonce = response.headers.get("dpop-nonce");
      if (nonce) response = await request(nonce);
    }
    return response;
  }
}

function findExit(value: unknown): { requestId: number; success: boolean; value?: unknown; error: string } | null {
  const object = value && typeof value === "object" ? (value as Record<string, unknown>) : null;
  if (!object || object._tag !== "Exit" || typeof object.requestId !== "number") return null;
  const exit = object.exit && typeof object.exit === "object" ? (object.exit as Record<string, unknown>) : {};
  if (exit._tag === "Success") return { requestId: object.requestId, success: true, value: exit.value, error: "" };
  const cause = Array.isArray(exit.cause) ? exit.cause : [];
  const failure = cause.find((entry) => entry && typeof entry === "object" && (entry as Record<string, unknown>)._tag === "Fail") as Record<string, unknown> | undefined;
  const error = failure?.error && typeof failure.error === "object" ? (failure.error as Record<string, unknown>).message : undefined;
  return { requestId: object.requestId, success: false, error: typeof error === "string" ? error : "Remote RPC request failed." };
}

async function assertOk(response: Response, message: string): Promise<void> {
  if (response.ok) return;
  const detail = await response.text().catch(() => "");
  throw new Error(`${message} (${response.status})${detail ? `: ${detail.slice(0, 240)}` : ""}`);
}

function stringValue(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function toWebSocketUrl(base: string, suffix = ""): string {
  const url = new URL(base);
  url.protocol = url.protocol === "https:" ? "wss:" : url.protocol === "http:" ? "ws:" : url.protocol;
  if (suffix) url.pathname = `${url.pathname.replace(/\/$/, "")}${suffix}`;
  if (!suffix && (!url.pathname || url.pathname === "/")) url.pathname = "/ws";
  return url.toString();
}

function appendQuery(url: string, key: string, value: string): string {
  const parsed = new URL(url);
  parsed.searchParams.set(key, value);
  return parsed.toString();
}
